import React, { useState, useRef, useEffect } from 'react';
import { exportToCSV, exportToExcel, exportToPDF, printPage } from '../exportUtils';
import { accountingApi } from '../../../services/accountingApi';

const SECTION_TABS = ['All', 'sell', 'sell_payment', 'purchase', 'purchase_payment', 'expense'];
const TAB_LABELS   = { All: 'All', sell: 'Sell', sell_payment: 'Sales Payments', purchase: 'Purchases', purchase_payment: 'Purchase Payments', expense: 'Expenses' };

/* ─── Status Badge ─────────────────────────────────────────── */
const StatusBadge = ({ status }) => {
  const styles = {
    Paid:    { background: '#dcfce7', color: '#166534' },
    Due:     { background: '#fee2e2', color: '#991b1b' },
    Partial: { background: '#fef9c3', color: '#854d0e' },
    Overdue: { background: '#ffe4e6', color: '#9f1239' },
  };
  const s = styles[status] || { background: '#f1f5f9', color: '#475569' };
  return (
    <span style={{ ...s, padding: '0.25rem 0.65rem', borderRadius: '999px', fontSize: '0.75rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
      {status}
    </span>
  );
};

/* ─── Main Component ───────────────────────────────────────── */
const Transactions = () => {
  const [activeSection, setActiveSection]   = useState('All');
  const [allData, setAllData]               = useState([]);
  const [loading, setLoading]               = useState(true);
  const [error, setError]                   = useState('');
  const [search, setSearch]                 = useState('');
  const [entries, setEntries]               = useState(25);
  const [showExport, setShowExport]         = useState(false);
  const [showFilter, setShowFilter]         = useState(false);
  const [filterStatus, setFilterStatus]     = useState('All');
  const [filterMethod, setFilterMethod]     = useState('All');
  const exportRef  = useRef(null);

  useEffect(() => {
    accountingApi.getTransactions()
      .then(res => {
        const arr = Array.isArray(res) ? res
          : Array.isArray(res.data) ? res.data
          : Array.isArray(res.data?.data) ? res.data.data
          : [];
        setAllData(arr);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  /* Close export dropdown on outside click */
  useEffect(() => {
    const handler = (e) => { if (exportRef.current && !exportRef.current.contains(e.target)) setShowExport(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  /* Filtered data */
  const sectionData = activeSection === 'All' ? allData : allData.filter(r => (r.type || r.transaction_type || '') === activeSection);
  const filtered = sectionData.filter(r => {
    const term = search.toLowerCase();
    const matchSearch = (r.contact_name || r.customer || '').toLowerCase().includes(term) ||
                        (r.invoice_no || r.ref_no || '').toLowerCase().includes(term);
    const status = r.payment_status || r.paymentStatus || '';
    const method = r.payment_method || r.paymentMethod || '';
    const matchStatus = filterStatus === 'All' || status === filterStatus;
    const matchMethod = filterMethod === 'All' || method === filterMethod;
    return matchSearch && matchStatus && matchMethod;
  }).slice(0, entries);

  /* Export handler */
  const handleExport = (format) => {
    if (!filtered.length) { alert('No data to export'); setShowExport(false); return; }
    const headers = ['Invoice No.', 'Date', 'Contact', 'Payment Status', 'Payment Method', 'Total Amount', 'Total Paid'];
    const rows = filtered.map(r => [
      r.invoice_no || r.ref_no || '—',
      r.transaction_date || r.date || '—',
      r.contact_name || r.customer || '—',
      r.payment_status || r.paymentStatus || '—',
      r.payment_method || r.paymentMethod || '—',
      (parseFloat(r.final_total || r.totalAmount) || 0).toFixed(2),
      (parseFloat(r.total_paid || r.totalPaid) || 0).toFixed(2),
    ]);
    const title    = `Transactions — ${TAB_LABELS[activeSection] || activeSection}`;
    const filename = `transactions_${activeSection}`;
    if (format === 'CSV')   exportToCSV(headers, rows, filename);
    if (format === 'Excel') exportToExcel(headers, rows, filename);
    if (format === 'PDF')   exportToPDF(title, headers, rows, filename);
    if (format === 'Print') printPage();
    setShowExport(false);
  };

  const resetFilters = () => { setFilterStatus('All'); setFilterMethod('All'); };

  if (loading) return <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>Loading transactions...</div>;
  if (error)   return <div style={{ padding: '2rem', textAlign: 'center', color: '#ef4444' }}>Error: {error}</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

      {/* ── Section Tabs Pill Bar ── */}
      <div style={{ display: 'flex', gap: '0.5rem', overflowX: 'auto', paddingBottom: '0.25rem' }}>
        {SECTION_TABS.map(tab => (
          <button
            key={tab}
            onClick={() => { setActiveSection(tab); setSearch(''); setFilterStatus('All'); setFilterMethod('All'); }}
            style={{
              padding: '0.6rem 1.25rem',
              borderRadius: '999px',
              fontSize: '0.875rem',
              fontWeight: 600,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              border: 'none',
              background: activeSection === tab ? 'var(--acc-primary)' : 'var(--acc-surface)',
              color: activeSection === tab ? '#fff' : 'var(--acc-text-main)',
              boxShadow: activeSection === tab ? '0 4px 12px rgba(99, 102, 241, 0.3)' : 'var(--acc-shadow-sm)',
              transition: 'all 0.2s ease'
            }}
          >
            {TAB_LABELS[tab] || tab}
          </button>
        ))}
      </div>

      {/* ── Card wrapper ── */}
      <div className="table-content-card" style={{ padding: 0, overflow: 'hidden' }}>

        {/* ── Toolbar ── */}
        <div className="table-controls" style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--acc-border)', background: 'var(--acc-surface)' }}>
          {/* Left: entries */}
          <div className="table-controls-left" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--acc-text-muted)' }}>Show</span>
            <select
              value={entries}
              onChange={e => setEntries(Number(e.target.value))}
              style={{ height: '40px', padding: '0 0.75rem', border: '1px solid var(--acc-border)', borderRadius: 'var(--acc-radius-sm)', fontSize: '0.875rem', outline: 'none', background: 'var(--acc-surface)' }}
            >
              {[10, 25, 50, 100].map(n => <option key={n}>{n}</option>)}
            </select>
            <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--acc-text-muted)' }}>entries</span>
          </div>

          {/* Right: search + filter + export */}
          <div className="table-controls-right">
            {/* Search */}
            <div className="tabs-search-wrapper">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--acc-text-muted)', marginLeft: '0.5rem' }}>
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                className="tabs-search-input"
                placeholder="Search transactions..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>

            {/* Filter button */}
            <button
              className="acc-btn-secondary"
              onClick={() => setShowFilter(true)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{ opacity: 0.8 }}><path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z"/></svg>
              Filters
            </button>

            {/* Export dropdown */}
            <div ref={exportRef} style={{ position: 'relative' }}>
              <button
                className="acc-btn-secondary"
                onClick={() => setShowExport(p => !p)}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.8 }}>
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                Export ▾
              </button>
              {showExport && (
                <div style={{ position: 'absolute', right: 0, top: 'calc(100% + 0.5rem)', background: 'var(--acc-surface)', border: '1px solid var(--acc-border)', borderRadius: 'var(--acc-radius)', boxShadow: 'var(--acc-shadow-lg)', zIndex: 50, minWidth: '160px', overflow: 'hidden' }}>
                  {['CSV','Excel','PDF','Print'].map(f => (
                    <div key={f} onClick={() => handleExport(f)}
                      style={{ padding: '0.75rem 1rem', cursor: 'pointer', fontSize: '0.9rem', color: 'var(--acc-text-main)', borderBottom: f !== 'Print' ? '1px solid var(--acc-border)' : 'none', transition: 'background 0.2s' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--acc-bg)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      Export {f}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Active filter badges ── */}
        {(filterStatus !== 'All' || filterMethod !== 'All') && (
          <div style={{ padding: '0.75rem 1.5rem', display: 'flex', gap: '0.75rem', flexWrap: 'wrap', borderBottom: '1px solid var(--acc-border)', background: 'var(--acc-bg)', alignItems: 'center' }}>
            <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--acc-text-muted)' }}>Active Filters:</span>
            {filterStatus !== 'All' && (
              <span className="acc-badge acc-badge-blue">
                Status: {filterStatus} <span style={{ cursor: 'pointer', marginLeft: '6px', fontSize: '1rem', lineHeight: 1 }} onClick={() => setFilterStatus('All')}>×</span>
              </span>
            )}
            {filterMethod !== 'All' && (
              <span className="acc-badge acc-badge-blue">
                Method: {filterMethod} <span style={{ cursor: 'pointer', marginLeft: '6px', fontSize: '1rem', lineHeight: 1 }} onClick={() => setFilterMethod('All')}>×</span>
              </span>
            )}
            <button onClick={resetFilters} style={{ background: 'none', border: 'none', color: 'var(--acc-danger)', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, transition: 'opacity 0.2s' }} onMouseEnter={e => e.target.style.opacity = 0.8} onMouseLeave={e => e.target.style.opacity = 1}>Clear All</button>
          </div>
        )}

        {/* ── Table ── */}
        <div className="acc-table-wrapper">
          <table className="acc-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Invoice No.</th>
                <th>Type</th>
                <th>Contact</th>
                <th>Payment Status</th>
                <th>Payment Method</th>
                <th className="text-right">Total Amount</th>
                <th className="text-right">Total Paid</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r, i) => (
                <tr key={r.id || i}>
                  <td style={{ fontSize: '0.85rem', whiteSpace: 'nowrap', fontWeight: 500, color: 'var(--acc-text-muted)' }}>{r.transaction_date || r.date || '—'}</td>
                  <td><span style={{ fontWeight: 700, color: 'var(--acc-primary)' }}>{r.invoice_no || r.ref_no || '—'}</span></td>
                  <td>
                    <span style={{ background: 'var(--acc-bg)', padding: '0.2rem 0.5rem', borderRadius: '4px', fontSize: '0.8rem', fontWeight: 600, color: 'var(--acc-text-main)', textTransform: 'capitalize' }}>
                      {r.type || r.transaction_type || '—'}
                    </span>
                  </td>
                  <td style={{ fontWeight: 600 }}>{r.contact_name || r.customer || '—'}</td>
                  <td><StatusBadge status={r.payment_status || r.paymentStatus || '—'} /></td>
                  <td style={{ fontWeight: 500, color: 'var(--acc-text-muted)' }}>{r.payment_method || r.paymentMethod || '—'}</td>
                  <td className="text-right" style={{ fontWeight: 700 }}>$ {(parseFloat(r.final_total || r.totalAmount) || 0).toFixed(2)}</td>
                  <td className="text-right" style={{ fontWeight: 700, color: parseFloat(r.total_paid || r.totalPaid) >= parseFloat(r.final_total || r.totalAmount) ? 'var(--acc-success)' : 'var(--acc-warning)' }}>$ {(parseFloat(r.total_paid || r.totalPaid) || 0).toFixed(2)}</td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={8}>
                    <div className="acc-empty">
                      <div className="acc-empty-icon">💳</div>
                      <h4>No transactions found</h4>
                      <p>Try adjusting your search or filters to find what you're looking for.</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* ── Pagination footer ── */}
        <div style={{ padding: '1rem 1.5rem', background: 'var(--acc-surface)', borderTop: '1px solid var(--acc-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
          <span style={{ fontSize: '0.85rem', fontWeight: 500, color: 'var(--acc-text-muted)' }}>Showing <strong style={{ color: 'var(--acc-text-main)' }}>{filtered.length}</strong> of <strong style={{ color: 'var(--acc-text-main)' }}>{sectionData.length}</strong> entries</span>
          {sectionData.length > entries && (
            <button onClick={() => setEntries(sectionData.length)} className="acc-btn-secondary" style={{ padding: '0.4rem 1rem', fontSize: '0.8rem' }}>
              Show All
            </button>
          )}
        </div>
      </div>

      {/* ── Filter Modal ── */}
      {showFilter && (
        <div
          onClick={() => setShowFilter(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(4px)' }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: 'var(--acc-surface)', borderRadius: 'var(--acc-radius-lg)', width: '95%', maxWidth: '480px', overflow: 'hidden', boxShadow: 'var(--acc-shadow-lg)', animation: 'fadeIn 0.3s ease' }}
          >
            <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--acc-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--acc-bg)' }}>
              <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 800, color: 'var(--acc-text-main)' }}>Filter Transactions</h3>
              <button onClick={() => setShowFilter(false)} style={{ background: 'var(--acc-surface)', border: '1px solid var(--acc-border)', borderRadius: '50%', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--acc-text-muted)', transition: 'all 0.2s' }} onMouseEnter={e => { e.currentTarget.style.color = 'var(--acc-danger)'; e.currentTarget.style.borderColor = 'var(--acc-danger)' }} onMouseLeave={e => { e.currentTarget.style.color = 'var(--acc-text-muted)'; e.currentTarget.style.borderColor = 'var(--acc-border)' }}>✕</button>
            </div>
            <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
              <div className="acc-form-group">
                <label>Payment Status</label>
                <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                  {['All', 'Paid', 'Due', 'Partial', 'Overdue'].map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div className="acc-form-group">
                <label>Payment Method</label>
                <select value={filterMethod} onChange={e => setFilterMethod(e.target.value)}>
                  {['All', 'Cash', 'Card', 'Bank'].map(m => <option key={m}>{m}</option>)}
                </select>
              </div>
            </div>
            <div style={{ padding: '1.25rem 1.5rem', borderTop: '1px solid var(--acc-border)', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', background: 'var(--acc-bg)' }}>
              <button className="acc-btn-secondary" onClick={() => { resetFilters(); setShowFilter(false); }}>
                Reset Filters
              </button>
              <button className="acc-btn-primary" onClick={() => setShowFilter(false)}>
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Transactions;
